import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Edit3, Check, X, ExternalLink, Eye, EyeOff, Trash2 } from 'lucide-react';
import { useDrag } from '@use-gesture/react';
import Calendar from '../../components/shared/ui/Calendar';
import { createPortal } from 'react-dom';
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/react';
import { formatYMD, parseLocalDate } from '../../utils/datePlain';
import { canonicalFrom } from '../../utils/canonicalUrl';
import { logger } from '../../utils/logger';
import { PlatformKey } from '../../utils/platformConfig';


interface Carousel3DProps<T> {
  items: T[];
  renderEmbed: (item: T, isActive: boolean) => React.ReactNode;
  onUpdate?: (item: T) => void;
  onDelete?: (id: string) => void;
  canEdit?: boolean;
  getTitle: (item: T) => string;
  getDate: (item: T) => string;
  getUrl: (item: T) => string | null;
  getId: (item: T) => string;
  getApproved?: (item: T) => boolean;
  getPlatform?: (item: T) => string;
}

export function Carousel3D<T>({
  items,
  renderEmbed,
  onUpdate,
  onDelete,
  canEdit = false,
  getTitle,
  getDate,
  getUrl,
  getId,
  getApproved,
  getPlatform
}: Carousel3DProps<T>) {
  const [activeIndex, setActiveIndex] = useState(0);
  
  
  // Enhanced mobile and performance detection
  const deviceInfo = useMemo(() => {
    const userAgent = navigator.userAgent;
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    
    // Device capabilities
    const hardwareConcurrency = navigator.hardwareConcurrency || 4;
    // Use feature detection with proper types
    interface NetworkInformation {
      effectiveType?: string;
      downlink?: number;
    }
    interface NavigatorWithDeviceMemory extends Navigator {
      deviceMemory?: number;
      connection?: NetworkInformation;
      mozConnection?: NetworkInformation;
      webkitConnection?: NetworkInformation;
    }
    const deviceMemory = (navigator as NavigatorWithDeviceMemory).deviceMemory ?? 4; // GB
    const isOldAndroid = /android.*4\.[0-3]|android.*5\.[0-1]/i.test(userAgent);
    
    // Network capabilities
    const connection = (navigator as NavigatorWithDeviceMemory).connection || 
                       (navigator as NavigatorWithDeviceMemory).mozConnection || 
                       (navigator as NavigatorWithDeviceMemory).webkitConnection;
    const isSlowConnection = connection && (
      connection.effectiveType === 'slow-2g' ||
      connection.effectiveType === '2g' ||
      connection.effectiveType === '3g' ||
      (connection.downlink !== undefined && connection.downlink <= 1.5) // 1.5 Mbps or less
    );
    
    const isLowEnd = isMobile && (
      hardwareConcurrency <= 2 || // 2 or fewer CPU cores
      deviceMemory <= 2 || // 2GB or less RAM
      isOldAndroid
    );
    
    return {
      isMobile,
      isLowEnd,
      isSlowConnection,
      hardwareConcurrency,
      deviceMemory,
      connectionType: connection?.effectiveType || 'unknown'
    };
  }, []);
  
  // Adaptive performance settings based on device capabilities
  const performanceSettings = useMemo(() => {
    const { isMobile, isLowEnd, isSlowConnection } = deviceInfo;
    
    // Animation durations (shorter for low-end devices)
    const animationDuration = isLowEnd ? 0.2 : isMobile ? 0.4 : 0.6;
    
    // Swipe sensitivity (more sensitive for low-end devices)
    const swipeDuration = isLowEnd ? 100 : isMobile ? 150 : 200;
    const swipeVelocity = isLowEnd ? 0.2 : isMobile ? 0.25 : 0.3;
    
    // Preload settings (fewer items for slow connections)
    const preloadCount = isSlowConnection ? 1 : isMobile ? 2 : 3;
    
    // Reduced motion for accessibility
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    return {
      animationDuration: prefersReducedMotion ? 0.1 : animationDuration,
      swipeDuration,
      swipeVelocity,
      preloadCount,
      enableAnimations: !prefersReducedMotion,
      enableParallax: !isLowEnd && !prefersReducedMotion
    };
  }, [deviceInfo]);
  
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDate, setEditDate] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  
  // Lazy loading state for embeds
  const [loadedEmbeds, setLoadedEmbeds] = useState<Set<number>>(new Set());
  
  // Load embeds based on proximity to active card
  useEffect(() => {
    const { preloadCount } = performanceSettings;
    const newLoadedEmbeds = new Set<number>();
    
    // Always load the active card
    newLoadedEmbeds.add(activeIndex);
    
    // Load nearby cards based on device capabilities
    for (let i = 1; i <= preloadCount; i++) {
      // Load cards to the left
      const leftIndex = (activeIndex - i + items.length) % items.length;
      newLoadedEmbeds.add(leftIndex);
      
      // Load cards to the right
      const rightIndex = (activeIndex + i) % items.length;
      newLoadedEmbeds.add(rightIndex);
    }
    
    setLoadedEmbeds(newLoadedEmbeds);
  }, [activeIndex, items.length, performanceSettings]);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const dateInputRef = useRef<HTMLSpanElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const modalFirstRef = useRef<HTMLButtonElement>(null);
  const editFirstRef = useRef<HTMLInputElement>(null);

  // Floating UI for calendar
  const { x, y, refs, strategy } = useFloating({
    placement: 'bottom-start',
    middleware: [offset(8), flip(), shift()],
    whileElementsMounted: autoUpdate
  });

  // Navigate to next/prev
  const navigate = useCallback((direction: 'next' | 'prev') => {
    setActiveIndex(prev => {
      if (direction === 'next') {
        return (prev + 1) % items.length;
      } else {
        return (prev - 1 + items.length) % items.length;
      }
    });
  }, [items.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (editingId) return; // Don't navigate while editing
      if (e.key === 'ArrowLeft') navigate('prev');
      if (e.key === 'ArrowRight') navigate('next');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, editingId]);

  // Adjust active index when items are deleted
  useEffect(() => {
    if (items.length === 0) {
      setActiveIndex(0);
    } else if (activeIndex >= items.length) {
      setActiveIndex(items.length - 1);
    }
  }, [items.length, activeIndex]);

  // Drag gesture for swipe with adaptive settings
  const bind = useDrag(
    ({ swipe: [swipeX] }) => {
      if (swipeX < 0) navigate('next');
      if (swipeX > 0) navigate('prev');
    },
    {
      axis: 'x',
      swipe: { 
        duration: performanceSettings.swipeDuration, 
        velocity: performanceSettings.swipeVelocity 
      }
    }
  );

  // Calculate position for each card - memoized to prevent recalculation on every render
  const getPosition = useCallback((index: number): 'active' | 'left' | 'right' | 'far-left' | 'far-right' => {
    const diff = index - activeIndex;
    const len = items.length;
    
    if (diff === 0) return 'active';
    if (diff === -1 || diff === len - 1) return 'left';
    if (diff === 1 || diff === -(len - 1)) return 'right';
    if (diff === -2 || diff === len - 2) return 'far-left';
    if (diff === 2 || diff === -(len - 2)) return 'far-right';
    return 'far-left'; // All others hidden far left
  }, [activeIndex, items.length]);

  // Start editing
  const startEdit = (item: T) => {
    if (!canEdit) return;
    const id = getId(item);
    lastFocusedRef.current = document.activeElement as HTMLElement;
    setEditingId(id);
    setEditTitle(getTitle(item));
    setEditDate(getDate(item));
  };

  // Save edit
  const saveEdit = () => {
    if (!editingId || !onUpdate) return;
    const item = items.find(i => getId(i) === editingId);
    if (item) {
      onUpdate({ ...item, title: editTitle, postedAt: editDate } as T);
    }
    setEditingId(null);
    setShowCalendar(false);
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingId(null);
    setShowCalendar(false);
    // restore focus
    requestAnimationFrame(() => lastFocusedRef.current?.focus?.());
  };

  // Toggle visibility (approved status)
  const toggleVisibility = (item: T) => {
    if (!canEdit || !onUpdate || !getApproved) return;
    const newApproved = !getApproved(item);
    onUpdate({ ...item, approved: newApproved } as T);
  };

  // Delete asset
  const confirmDelete = (id: string) => {
    if (!onDelete) return;
    onDelete(id);
    setShowDeleteConfirm(null);
  };

  // Keyboard shortcuts for delete confirmation modal
  useEffect(() => {
    if (!showDeleteConfirm) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        confirmDelete(showDeleteConfirm);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowDeleteConfirm(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showDeleteConfirm]);

  // Type guard for items with platform property
  const getItemPlatform = (item: T): string | undefined => {
    if (typeof item === 'object' && item !== null && 'platform' in item) {
      const platform = (item as Record<string, unknown>).platform;
      return typeof platform === 'string' ? platform.toLowerCase() : undefined;
    }
    return undefined;
  };

  // Get platform-specific overlay class for proper positioning
  const getPlatformOverlayClass = (item: T): string => {
    const platform = getItemPlatform(item);
    if (!platform) return '';
    
    switch (platform) {
      case 'youtube':
      case 'youtube-shorts':
        return 'youtube-overlay';
      case 'facebook':
        return 'facebook-overlay';
      case 'instagram':
        return 'instagram-overlay';
      case 'tiktok':
        return 'tiktok-overlay';
      case 'twitter':
        return 'twitter-overlay';
      default:
        return '';
    }
  };

  // CHANGED: Only Instagram/Twitter scroll
  const isScrollablePlatform = (item: T): boolean => {
    const p = getItemPlatform(item);
    return p === 'instagram' || p === 'twitter';
  };

  const getPlatformScrollableClass = (item: T): string => {
    const platform = getItemPlatform(item);
    if (!platform) return '';
    switch (platform) {
      case 'instagram':
        return 'instagram-scrollable';
      case 'twitter':
        return 'twitter-scrollable';
      default:
        return '';
    }
  };

  // Click outside calendar to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (refs.floating.current && refs.floating.current.contains(target)) return;
      if (dateInputRef.current && dateInputRef.current.contains(target)) return;
      setShowCalendar(false);
    };

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [showCalendar]);

  // Apply device-specific CSS classes
  const wrapperClasses = useMemo(() => {
    const classes = ['carousel-3d-wrapper'];
    if (deviceInfo.isLowEnd) classes.push('low-end-device');
    if (deviceInfo.isMobile) classes.push('mobile-device');
    if (deviceInfo.isSlowConnection) classes.push('slow-connection');
    return classes.join(' ');
  }, [deviceInfo]);

  return (
    <div className={wrapperClasses} ref={containerRef}>
      {/* 3D Carousel Stage */}
      <div data-test="carousel-3d" className="carousel-3d-container" {...bind()}>
        <div className="carousel-3d-stage">
          <AnimatePresence initial={false}>
            {items.map((item, index) => {
              const position = getPosition(index);
              const id = getId(item);
              const isActive = position === 'active';
              const isEditing = editingId === id;
              const url = getUrl(item);
              const platform = getPlatform?.(item) || 'link';
              const canonicalUrl = url && platform ? canonicalFrom(platform as PlatformKey, url) : null;

              return (
                <motion.div
                  key={id}
                  className={`carousel-card carousel-card-${position}`}
                  initial={false}
                  animate={position}
                  variants={{
                    active: {
                      x: '0%',
                      z: 250,
                      rotateY: 0,
                      scale: 1,
                      opacity: 1,
                      zIndex: 20
                    },
                    left: {
                      x: '-70%',
                      z: -150,
                      rotateY: 35,
                      scale: 0.75,
                      opacity: 0.5,
                      zIndex: 10
                    },
                    right: {
                      x: '70%',
                      z: -150,
                      rotateY: -35,
                      scale: 0.75,
                      opacity: 0.5,
                      zIndex: 10
                    },
                    'far-left': {
                      x: '-100%',
                      z: -300,
                      rotateY: 45,
                      scale: 0.5,
                      opacity: 0,
                      zIndex: 1
                    },
                    'far-right': {
                      x: '100%',
                      z: -300,
                      rotateY: -45,
                      scale: 0.5,
                      opacity: 0,
                      zIndex: 1
                    }
                  }}
                  transition={{
                    duration: performanceSettings.animationDuration,
                    ease: [0.4, 0, 0.2, 1]
                  }}
                  onClick={() => {
                    if (position === 'left') {
                      navigate('prev');
                    }
                    if (position === 'right') {
                      navigate('next');
                    }
                  }}
                  style={{
                    cursor: position === 'active' ? 'default' : 'pointer'
                  }}
                >
                  {/* Card Content */}
                  <div className={`carousel-card-inner ${isEditing ? 'editing' : ''}`}>
                    {/* CHANGED: pick the correct content container */}
                    <div
                      className={`${isScrollablePlatform(item) ? 'carousel-embed-content-scrollable' : 'carousel-embed-content'} absolute inset-0 ${isScrollablePlatform(item) ? getPlatformScrollableClass(item) : ''}`}
                      onWheelCapture={(e) => e.stopPropagation()}
                      onTouchMoveCapture={(e) => e.stopPropagation()}
                    >
                      {loadedEmbeds.has(index) ? (
                        renderEmbed(item, isActive)
                      ) : (
                        <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-gray-800 rounded-lg">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Loading...</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Transparent Overlay - Only visible on active card */}
                    {isActive && (
                      <>

                        {/* Title and Date Overlay - Bottom (semi-transparent) */}
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                          className={`carousel-card-overlay-transparent ${getPlatformOverlayClass(item)}`}
                        >
                          {!isEditing ? (
                            <div className="carousel-card-overlay-content">
                              {/* Line 1: Title (always clickable to original source) */}
                              <a
                                href={canonicalUrl || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!canonicalUrl || canonicalUrl === '#') {
                                    e.preventDefault();
                                    if (import.meta.env.DEV) {
                                      const itemRecord = typeof item === 'object' && item !== null ? item as Record<string, unknown> : {};
                                      logger.warn('No valid canonical URL found for asset', {
                                        title: getTitle(item),
                                        url,
                                        platform,
                                        embedCodes: itemRecord.embedCodes,
                                        originalUrl: itemRecord.originalUrl
                                      }, 'Carousel3D');
                                    }
                                  } else {
                                    if (import.meta.env.DEV) {
                                      logger.debug('Opening canonical URL', { canonicalUrl }, 'Carousel3D');
                                    }
                                  }
                                }}
                                className="clickable-carousel-title inline-flex items-center gap-1.5 text-sm sm:text-base truncate cursor-pointer"
                                title={canonicalUrl ? `Open ${canonicalUrl}` : 'No URL available'}
                              >
                                <span className="truncate">{getTitle(item)}</span>
                                <ExternalLink size={18} className="flex-shrink-0" />
                              </a>

                              {/* Line 2: Date left · Admin actions right */}
                              <div className="mt-1 flex items-center gap-2">
                                <p className="text-xs shrink-0">
                                  {(() => {
                                    const raw = getDate(item);
                                    const d = parseLocalDate(raw) ?? new Date(raw);
                                    return isNaN(d.getTime()) ? '—' :
                                      d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                                  })()}
                                </p>

                                {getApproved && !getApproved(item) && (
                                  <span className="ml-2 px-2 py-0.5 rounded-full text-[11px] font-medium bg-yellow-400/95 text-yellow-950">
                                    Hidden
                                  </span>
                                )}

                                <div className="ml-auto flex items-center gap-1">
                                  {canEdit && getApproved && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); toggleVisibility(item); }}
                                      className="clickable-icon-btn asset-icon-btn" 
                                      title={getApproved(item) ? 'Hide from viewers' : 'Show to viewers'}
                                      aria-label={getApproved(item) ? 'Hide from viewers' : 'Show to viewers'}
                                    >
                                      {getApproved(item) ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                                    </button>
                                  )}
                                  {canEdit && (
                                    <>
                                      <button 
                                        onClick={(e) => { e.stopPropagation(); startEdit(item); }} 
                                        className="clickable-icon-btn asset-icon-btn" 
                                        title="Edit"
                                        aria-label="Edit asset"
                                      >
                                        <Edit3 className="w-5 h-5" />
                                      </button>
                                      <button 
                                        onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(getId(item)); }} 
                                        className="clickable-icon-btn asset-icon-btn" 
                                        title="Delete"
                                        aria-label="Delete asset"
                                      >
                                        <Trash2 className="w-5 h-5" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                        ) : (
                          // Edit Mode
                          <div
                            className="carousel-card-overlay-content"   // << this restores pointer events
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="space-y-3">
                            {/* Title Input */}
                            <div>
                              <label className="block text-[11px] uppercase tracking-wider text-white/60 mb-1.5 font-medium">Title</label>
                              <input
                                type="text"
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => e.stopPropagation()}
                                ref={editFirstRef}
                                className="w-full px-3 py-2.5 bg-black/60 text-white rounded-lg border border-white/20 focus:outline-none focus:border-white/50 focus:bg-black/70 font-medium backdrop-blur-sm transition-all"
                                placeholder="Enter title..."
                                autoFocus
                              />
                            </div>

                            {/* Date Input */}
                            <div>
                              <label className="block text-[11px] uppercase tracking-wider text-white/60 mb-1.5 font-medium">Date Posted</label>
                              <span
                                ref={(el) => {
                                  if (el) {
                                    refs.setReference(el);
                                    dateInputRef.current = el;
                                  }
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowCalendar(!showCalendar);
                                }}
                                className="flex items-center gap-2 px-3 py-2.5 bg-black/60 text-white rounded-lg border border-white/20 cursor-pointer hover:bg-black/70 hover:border-white/40 font-medium backdrop-blur-sm transition-all"
                              >
                                <CalendarIcon size={16} />
                                {editDate ? (parseLocalDate(editDate)?.toLocaleDateString() || 'Invalid date') : 'Select date'}
                              </span>

                              {showCalendar && createPortal(
                                <div
                                  ref={refs.setFloating}
                                  data-floating-ui-portal
                                  style={{
                                    position: strategy,
                                    top: y ?? 0,
                                    left: x ?? 0,
                                    zIndex: 10000,
                                    backgroundColor: 'var(--bg-elev)',
                                    border: '1px solid var(--border)',
                                    borderRadius: '12px',
                                    boxShadow: 'var(--shadow-strong)',
                                    backdropFilter: 'none',
                                    WebkitBackdropFilter: 'none',
                                    opacity: 1,
                                    isolation: 'isolate'
                                  }}
                                  className="p-2 text-[var(--text)]"
                                >
                                  <Calendar
                                    value={editDate ? (parseLocalDate(editDate) ?? new Date()) : new Date()}
                                    onChange={(date) => {
                                      setEditDate(formatYMD(date));
                                      setShowCalendar(false);
                                    }}
                                    onClose={() => setShowCalendar(false)}
                                    showMonthYearDropdown={true}
                                  />
                                </div>,
                                document.body
                              )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2 pt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  saveEdit();
                                }}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
                              >
                                <Check size={18} />
                                Save
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  cancelEdit();
                                }}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
                              >
                                <X size={18} />
                                Cancel
                              </button>
                            </div>
                            </div>
                          </div>
                          )}
                        </motion.div>
                      </>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="deleteTitle"
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100]"
            onClick={() => setShowDeleteConfirm(null)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setShowDeleteConfirm(null);
              if (e.key === 'Tab') {
                // simple focus trap
                const focusables = Array.from(
                  (e.currentTarget as HTMLElement).querySelectorAll<HTMLElement>(
                    'button,a,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'
                  )
                ).filter(el => !el.hasAttribute('disabled'));
                if (!focusables.length) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                  e.preventDefault(); last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                  e.preventDefault(); first.focus();
                }
              }
            }}
            ref={(el) => {
              if (el && modalFirstRef.current) modalFirstRef.current.focus();
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="theme-surface rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 id="deleteTitle" className="text-xl font-bold theme-text mb-3">Delete Asset?</h3>
              <p className="theme-text-muted mb-6">
                This action cannot be undone. The asset will be permanently removed.
              </p>
              <div className="flex gap-3">
                <button
                  ref={modalFirstRef}
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 px-4 py-2.5 theme-text-muted theme-muted hover:theme-muted rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmDelete(showDeleteConfirm)}
                  className="flex-1 px-4 py-2.5 text-white bg-red-600 hover:bg-red-700 rounded-lg font-medium transition-colors"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Navigation Buttons */}
        <button
          aria-label="Previous asset"
          onClick={() => navigate('prev')}
          className="carousel-nav-button carousel-nav-left"
        >
          <ChevronLeft size={32} />
        </button>

        <button
          aria-label="Next asset"
          onClick={() => navigate('next')}
          className="carousel-nav-button carousel-nav-right"
        >
          <ChevronRight size={32} />
        </button>
      </div>

      {/* Indicator Dots */}
      <div className="carousel-indicators">
        {items.map((item, index) => (
            <button
              key={getId(item)}
              onClick={() => {
                setActiveIndex(index);
              }}
              className={`carousel-dot ${index === activeIndex ? 'active' : ''}`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

